import { EntityProfileType } from "../../../types/entity";
import { accountTypeToAccountClass } from "./helpers";
import { AccountClass, AccountSubtype, AccountType } from "./accounts";
import {
    defaultCashChartOfAccounts,
    defaultCashChartOfAccountsFinancialAccountGroups,
    getSubtypesInsideType,
} from "./chartOfAccounts";

const SUBTYPE_TO_TYPES: ReadonlyMap<
    AccountSubtype,
    ReadonlySet<AccountType>
> = (() => {
    const map = new Map<AccountSubtype, Set<AccountType>>();
    for (const typeDeclaration of defaultCashChartOfAccounts) {
        for (const subtype of [
            ...typeDeclaration.subtypesStartFromBeginning,
            ...typeDeclaration.subtypesStartFromEnd,
        ]) {
            const types = map.get(subtype.key) ?? new Set<AccountType>();
            types.add(typeDeclaration.typeKey);
            map.set(subtype.key, types);
        }
    }
    return map;
})();

const SUBTYPE_TO_TYPE: ReadonlyMap<AccountSubtype, AccountType> = (() => {
    const map = new Map<AccountSubtype, AccountType>();
    for (const [subtype, types] of SUBTYPE_TO_TYPES) {
        const [firstType] = types;
        map.set(subtype, firstType);
    }
    return map;
})();

const KNOWN_SUBTYPES: ReadonlySet<string> = new Set(SUBTYPE_TO_TYPES.keys());

export function getAvailableAccountTypes(
    availableClassifications?: AccountClass[],
): AccountType[] {
    // operatingExpenses owns multiple seed blocks (the original opex range
    // plus the absorbed payroll ranges), so dedupe to avoid duplicate
    // entries in Type-picker UIs.
    const typeKeys = availableClassifications
        ? defaultCashChartOfAccounts
              .filter(({ accountClass }) =>
                  availableClassifications.includes(accountClass),
              )
              .map(({ typeKey }) => typeKey)
        : defaultCashChartOfAccounts.map(({ typeKey }) => typeKey);
    return Array.from(new Set(typeKeys));
}

export function getAccountTypeForSubtype(subtype: AccountSubtype): AccountType {
    const type = SUBTYPE_TO_TYPE.get(subtype);
    if (!type) {
        throw new Error(
            `Could not determine account type for subtype: ${subtype}`,
        );
    }
    return type;
}

export function getAccountSubtypesForType(
    entityType: EntityProfileType,
    accountType: AccountType,
): AccountSubtype[] {
    const subtypesInsideType = getSubtypesInsideType(entityType, accountType);
    return [
        ...subtypesInsideType.subtypesStartFromBeginning,
        ...subtypesInsideType.subtypesStartFromEnd,
    ].map((subtype) => subtype.key);
}

export function getDefaultSubtypeForType(
    entityType: EntityProfileType,
    accountType: AccountType,
): AccountSubtype {
    // The type's first subtype in declaration order (e.g. operatingExpenses -> salariesAndWages),
    // mirroring the create form default.
    const defaultSubtype = tryGetDefaultSubtypeForType(entityType, accountType);
    if (!defaultSubtype) {
        throw new Error(
            `No default subtype available for type ${accountType} and entity ${entityType}`,
        );
    }
    return defaultSubtype;
}

export function tryGetDefaultSubtypeForType(
    entityType: EntityProfileType,
    accountType: AccountType,
): AccountSubtype | undefined {
    const [firstSubtype] = getSubtypesInsideType(
        entityType,
        accountType,
    ).subtypesStartFromBeginning;
    return firstSubtype?.key;
}

export function getAvailableAccountSubtypes(
    entityType: EntityProfileType,
    availableClassifications?: AccountClass[],
): AccountSubtype[] {
    const availableTypes = getAvailableAccountTypes(availableClassifications);
    return availableTypes.flatMap((type) =>
        getAccountSubtypesForType(entityType, type),
    );
}

export function isValidAccountType(type: string | undefined): boolean {
    if (!type) {
        return false;
    }
    return defaultCashChartOfAccounts.some((t) => t.typeKey === type);
}

export function isValidAccountSubtype(subtype: string | undefined): boolean {
    if (!subtype) {
        return false;
    }
    return KNOWN_SUBTYPES.has(subtype);
}

export function getAccountClassForType(
    accountType: AccountType,
): AccountClass | null {
    if (accountType in accountTypeToAccountClass) {
        return accountTypeToAccountClass[accountType as AccountType];
    }
    return null;
}

export function getAccountClassForCode(code: number): AccountClass | null {
    const typeDeclaration = defaultCashChartOfAccounts.find(
        (type) => code >= type.startFrom && code <= type.upTo,
    );
    return typeDeclaration?.accountClass ?? null;
}

export function isAccountTypeValidForClass(
    accountType: AccountType,
    accountClass: AccountClass,
): boolean {
    const typeClass = getAccountClassForType(accountType);
    return typeClass === accountClass;
}

export function isSameClassification(
    accountType: AccountType,
    otherAccountType: AccountType,
): boolean {
    return (
        getAccountClassForType(accountType) ===
        getAccountClassForType(otherAccountType)
    );
}

export function isAccountSubtypeValidForType(
    accountSubtype: AccountSubtype,
    accountType: AccountType,
): boolean {
    const validTypes = SUBTYPE_TO_TYPES.get(accountSubtype);
    if (!validTypes) {
        return false;
    }
    for (const validType of validTypes) {
        if (validType === accountType) {
            return true;
        }
    }
    return false;
}

export function isFinancialAccountSubtype(
    accountSubtype: AccountSubtype,
): boolean {
    return accountSubtype in defaultCashChartOfAccountsFinancialAccountGroups;
}
